import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  fetchJobList, updateJob, trashJob, editJob,
} from "../../Adapters/ApiAdapter";
import ReactQuill from "react-quill";
import SeoTags from "../SeoTags/SeoTags";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CKEditorField from "../../components/CKEditorField";
import { baseUrl } from "../../variables/Variables";

const CareerJobs = () => {
  const [jobs, setJobs] = useState([]); // List of jobs
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editJobData, setEditJobData] = useState(null); // State for the job being edited
  const [deleteJobId, setDeleteJobId] = useState(null); // State for deleting a job
  const [saving, setSaving] = useState(false); // State for saving edits

  // Fetch Career Jobs List
  useEffect(() => {
    const loadJobs = async () => {
      try {
        const response = await fetchJobList();
        if (response?.data) {
          setJobs([...response.data].sort((a, b) => b.id - a.id)); // 🔄 Sort by latest first
        }
        else {
          setError("Failed to fetch job listings.");
        }
      } catch (err) {
        setError("An error occurred while fetching job listings.");
      } finally {
        setLoading(false);
      }
    };
    loadJobs();
  }, []);

  // Handle Edit Click
  const handleEdit = async (id) => {
    try {
      const response = await editJob(id);
      console.log("Edit Job Response:", response.data);
      if (response && response.data) {
        const jobs = response.data;
        setEditJobData({
          id: jobs.id,
          title: jobs.title,
          slug: jobs.slug,
          department: jobs.department,
          designation: jobs.designation,
          details: jobs.details,
          description: jobs.description,
          exp_required: jobs.exp_required,
          skills: jobs.skills,
          education: jobs.education,
          job_location: jobs.job_location,
          source_page: jobs.source_page,
          posted_by: jobs.posted_by,
          banner_img: jobs.banner_img,
          seo_title: jobs.seo_title,
          meta_title: jobs.meta_title,
          meta_description: jobs.meta_description,
          meta_keywords: jobs.meta_keywords,
          custom_scripts: jobs.custom_scripts,
          og_title: jobs.og_title,
          og_description: jobs.og_description,
          og_type: jobs.og_type,
          og_url: jobs.og_url,
          og_image: jobs.og_image,
          og_locale: jobs.og_locale,
          og_site_name: jobs.og_site_name,
          og_image_width: jobs.og_image_width,
          og_image_height: jobs.og_image_height,
          og_image_type: jobs.og_image_type,
          article_modified_time: jobs.article_modified_time,
          canonical_link: jobs.canonical_link,
          twitter_card: jobs.twitter_card,
          twitter_site: jobs.twitter_site,
          twitter_title: jobs.twitter_title,
          twitter_description: jobs.twitter_description,
          twitter_image: jobs.twitter_image,
        }); // Pre-fill the edit dialog with the fetched data
      } else {
        setError("Failed to fetch data for editing.");
      }
    } catch (err) {
      console.error("Error loading edit data:", err);
      setError("An error occurred while fetching edit data.");
    }
  };

  // Handle Save Edit
  const handleSaveEdit = async () => {
    setSaving(true);

    const formData = new FormData();
    const safeValue = (value) => (value === null || value === undefined ? "" : value);

    formData.append("title", safeValue(editJobData.title));
    formData.append("slug", safeValue(editJobData.slug));
    formData.append("department", safeValue(editJobData.department));
    formData.append("designation", safeValue(editJobData.designation));
    formData.append("details", safeValue(editJobData.details));
    formData.append("description", safeValue(editJobData.description));
    formData.append("exp_required", safeValue(editJobData.exp_required));
    formData.append("skills", safeValue(editJobData.skills));
    formData.append("education", safeValue(editJobData.education));
    formData.append("job_location", safeValue(editJobData.job_location));
    formData.append("source_page", safeValue(editJobData.source_page));
    formData.append("posted_by", safeValue(editJobData.posted_by));
    formData.append("seo_title", safeValue(editJobData.seo_title));
    formData.append("meta_title", safeValue(editJobData.meta_title));
    formData.append("meta_description", safeValue(editJobData.meta_description));
    formData.append("meta_keywords", safeValue(editJobData.meta_keywords));
    formData.append("custom_scripts", safeValue(editJobData.custom_scripts));
    formData.append("og_title", safeValue(editJobData.og_title));
    formData.append("og_description", safeValue(editJobData.og_description));
    formData.append("og_type", safeValue(editJobData.og_type));
    formData.append("og_url", safeValue(editJobData.og_url));
    formData.append("og_locale", safeValue(editJobData.og_locale));
    formData.append("og_site_name", safeValue(editJobData.og_site_name));
    formData.append("og_image_width", safeValue(editJobData.og_image_width));
    formData.append("og_image_height", safeValue(editJobData.og_image_height));
    formData.append("og_image_type", safeValue(editJobData.og_image_type));
    formData.append("article_modified_time", safeValue(editJobData.article_modified_time));
    formData.append("canonical_link", safeValue(editJobData.canonical_link));
    formData.append("twitter_card", safeValue(editJobData.twitter_card));
    formData.append("twitter_site", safeValue(editJobData.twitter_site));
    formData.append("twitter_title", safeValue(editJobData.twitter_title));
    formData.append("twitter_description", safeValue(editJobData.twitter_description));

    if (formData.banner_img instanceof File) {
      formData.append("banner_img", editJobData.banner_img);
    }
    if (formData.og_image instanceof File) {
      formData.append("og_image", editJobData.og_image);
    }
    if (formData.twitter_image instanceof File) {
      formData.append("twitter_image", editJobData.twitter_image);
    }

    try {
      const response = await updateJob(editJobData.id, formData);
      console.log("API Response", response);
      if (
        response &&
        (response.status === true || response.status === "true")
      ) {
        toast.success("Job Updated Sccessfully");

        // Update the jobs list with the new data
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === editJobData.id
              ? {
                ...job,
                title: editJobData.title,
                slug: editJobData.slug,
                department: editJobData.department,
                designation: editJobData.designation,
                details: editJobData.details,
                description: editJobData.description,
                exp_required: editJobData.exp_required,
                skills: editJobData.skills,
                education: editJobData.education,
                job_location: editJobData.job_location,
                source_page: editJobData.source_page,
                posted_by: editJobData.posted_by,
                banner_img: editJobData.banner_img,
                seo_title: editJobData.seo_title,
                meta_title: editJobData.meta_title,
                meta_description: editJobData.meta_description,
                meta_keywords: editJobData.meta_keywords,
                custom_scripts: editJobData.custom_scripts,
                og_title: editJobData.og_title,
                og_description: editJobData.og_description,
                og_type: editJobData.og_type,
                og_url: editJobData.og_url,
                og_image: editJobData.og_image,
                og_locale: editJobData.og_locale,
                og_site_name: editJobData.og_site_name,
                og_image_width: editJobData.og_image_width,
                og_image_height: editJobData.og_image_height,
                og_image_type: editJobData.og_image_type,
                article_modified_time: editJobData.article_modified_time,
                canonical_link: editJobData.canonical_link,
                twitter_card: editJobData.twitter_card,
                twitter_site: editJobData.twitter_site,
                twitter_title: editJobData.twitter_title,
                twitter_description: editJobData.twitter_description,
                twitter_image: editJobData.twitter_image,
              }
              : job
          )
        );
        setEditJobData(null); // Close the dialog
        setError(null); // Reset error
      } else {
        toast.error("Failed to Update Job");
      }
    } catch (err) {
      toast.error("Failed to save changes");
      setError("An error occurred while saving changes.");
    } finally {
      setSaving(false);
    }
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/&/g, " ")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-");
  };

  // Handle Delete
  const handleDelete = async (id) => {
    try {
      const response = await trashJob(id);
      if (response && response.status === "true") {
        toast.success("Job deleted successfully");
        setJobs((prevJobs) => prevJobs.filter((job) => job.id !== id));
        setDeleteJobId(null);
      } else {
        toast.error("Failed to delete job");
      }
    } catch (err) {
      console.error("Error deleting job:", err);
    }
  };

  // const stripHtml = (html) => html.replace(/<[^>]+>/g, "").trim();
  // DataGrid Columns
  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "title", headerName: "Title", width: 200 },
    { field: "department", headerName: "Department", width: 200 },
    { field: "designation", headerName: "Designation", width: 200 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row.id)} // Open the edit dialog
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => setDeleteJobId(params.row.id)} // Open delete confirmation
            style={{ marginLeft: "8px" }}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Career Jobs List
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <DataGrid rows={jobs} columns={columns} autoHeight />
      )}

      {/* Edit Dialog */}
      {editJobData && (
        <Dialog open onClose={() => setEditJobData(null)}>
          <DialogTitle>Edit Job</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Title"
              value={editJobData.title || ""}
              onChange={(e) => {
                const newTitle = e.target.value;
                setEditJobData({
                  ...editJobData,
                  title: newTitle,
                  slug: generateSlug(newTitle),
                });
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Slug"
              value={editJobData.slug}
              onChange={(e) =>
                setEditJobData({ ...editJobData, slug: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Department"
              value={editJobData.department || ""}
              onChange={(e) =>
                setEditJobData({ ...editJobData, department: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Designation"
              value={editJobData.designation || ""}
              onChange={(e) =>
                setEditJobData({ ...editJobData, designation: e.target.value })
              }
            />

            <Typography variant="body1">Details</Typography>
            {/* <ReactQuill
              theme="snow"
              value={editJobData.details || ""}
              onChange={(detail) =>
                setEditJobData({ ...editJobData, details: detail })
              }
              style={{ height: "200px", marginBottom: "50px" }}
            /> */}
            <CKEditorField
            value={editJobData.details}
            onChange={(data) => setEditJobData((prev) => ({ ...prev, details: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=career`}
          />

            <Typography variant="body1">Decription</Typography>
            {/* <ReactQuill
              theme="snow"
              value={editJobData.description || ""}
              onChange={(j) =>
                setEditJobData((prev) => ({ ...prev, description: j }))
              }
              style={{ height: "200px", marginBottom: "50px" }}
            /> */}
            <CKEditorField
            value={editJobData.description}
            onChange={(data) => setEditJobData((prev) => ({ ...prev, description: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=career`}
          />

            <TextField
              fullWidth
              margin="normal"
              label="Experience Required"
              type="text"
              value={editJobData.exp_required || ""}
              onChange={(e) =>
                setEditJobData({ ...editJobData, exp_required: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Skills"
              value={editJobData.skills || ""}
              onChange={(e) =>
                setEditJobData({ ...editJobData, skills: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Education"
              value={editJobData.education || ""}
              onChange={(e) =>
                setEditJobData({ ...editJobData, education: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="job_location"
              value={editJobData.job_location}
              onChange={(e) => {
                setEditJobData({
                  ...editJobData,
                  job_location: e.target.value,
                });
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="source_page"
              value={editJobData.source_page}
              onChange={(e) =>
                setEditJobData({ ...editJobData, source_page: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              value={editJobData.posted_by}
              label="posted_by"
              onChange={(e) =>
                setEditJobData({ ...editJobData, posted_by: e.target.value })
              }
            />
            <SeoTags formData={editJobData} setFormData={setEditJobData} />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditJobData(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      {deleteJobId && (
        <Dialog open onClose={() => setDeleteJobId(null)}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this job?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteJobId(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(deleteJobId)}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default CareerJobs;
